import type { FeedMediaItem } from '~/components/PropertyReelsFeed/types';
import type { ShortVideoProperty } from '~/core/entities/property';
import type {
	FeaturedVideosRecommendationType,
	ShortsFilters
} from '~/core/entities/property/filters';

import { mapPropertyToShortVideo } from '~/composables/shorts/mappers';
import { extractErrorLogContext, useLogger } from '~/composables/useLogger';
import {
	hasRecommendationReference,
	withDisplayInHomeShortsFallback
} from '~/composables/useShortsFilters/featuredVideosFilterRules';
import { SHORTS_SORT_BY } from '~/core/entities/property/filters';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';

export interface FeedCursor {
	filters: ShortsFilters;
	page: number;
	perPage: number;
	hasMore: boolean;
}

export interface FeedCounts {
	totalWithVideos: number;
	totalWithoutVideos: number;
}

export interface FeedMeta {
	allWatched?: boolean;
	counts?: FeedCounts;
	totalAvailable?: number;
	sectionTitle?: string;
	recommendationType?: FeaturedVideosRecommendationType;
}

/** Página transferível entre qualquer superfície que exibe Shorts. */
export interface FeedPage {
	items: FeedMediaItem<ShortVideoProperty>[];
	cursor: FeedCursor;
	meta: FeedMeta;
}

/** Nome legado mantido para consumidores fora desta refatoração. */
export type ShortVideo = FeedMediaItem<ShortVideoProperty>;
export type FetchShortsResult = FeedPage;

export const SHORTS_DEFAULT_PER_PAGE = 15;

export interface FetchShortsOptions {
	enableRecommendationFallback?: boolean;
}

// Intent prefetch: a hover on a shorts entry point can start the
// featured-videos request before navigation, and the page's own fetchVideos
// call (same filters/page/perPage) reuses the in-flight promise instead of
// re-requesting. Client-only, single entry, short TTL — a stale or mismatched
// prefetch is simply ignored and the normal fetch happens.
const PREFETCH_TTL_MS = 30_000;
let feedPrefetch: {
	key: string;
	promise: Promise<FeedPage>;
	ts: number;
} | null = null;

function feedPrefetchKey(
	filters: ShortsFilters,
	page: number,
	perPage: number
): string {
	return JSON.stringify([filters, page, perPage]);
}

function consumeFeedPrefetch(key: string): Promise<FeedPage> | null {
	if (!import.meta.client || !feedPrefetch) return null;
	const { key: cachedKey, promise, ts } = feedPrefetch;
	if (cachedKey !== key || Date.now() - ts > PREFETCH_TTL_MS) return null;
	feedPrefetch = null;
	return promise;
}

interface InternalFeedPage extends FeedPage {
	hasRequestError: boolean;
}

export function useFetchShorts() {
	const logger = useLogger('useFetchShorts');
	const { user } = useUserSession();
	const getUserId = () => {
		if (!import.meta.client) return undefined;
		return user.value?.id;
	};

	const getCookieData = useCookieData();

	async function requestVideos(
		filters: ShortsFilters,
		page: number,
		perPage: number
	): Promise<InternalFeedPage> {
		const [result, error] = await nuxtPropertiesRepository.getShorts(
			{ ...filters, page, perPage },
			getCookieData()
		);

		if (error || !result) {
			logger.error('Failed to fetch shorts', {
				...(error
					? extractErrorLogContext(error)
					: { error_message: 'No shorts result returned from repository' }),
				filters
			});
			return {
				items: [],
				cursor: { filters, page, perPage, hasMore: false },
				meta: {},
				hasRequestError: true
			};
		}

		const items = (result.properties ?? [])
			.map(mapPropertyToShortVideo)
			.filter((item): item is ShortVideo => item !== null);

		const pagination = result.pagination;
		const hasMorePages =
			(pagination?.page ?? 1) < (pagination?.totalPages ?? 1);

		return {
			items,
			cursor: { filters, page, perPage, hasMore: hasMorePages },
			meta: {
				allWatched: result.allWatched ?? Boolean(pagination && !hasMorePages),
				counts: result.counts,
				totalAvailable:
					result.totalAvailable ?? pagination?.filteredCount ?? items.length,
				sectionTitle: result.sectionTitle,
				recommendationType: result.recommendationType
			},
			hasRequestError: false
		};
	}

	async function fetchVideos(
		filters: ShortsFilters = {},
		page: number = 1,
		perPage?: number,
		options: FetchShortsOptions = {}
	): Promise<FeedPage> {
		const effectivePerPage = perPage ?? SHORTS_DEFAULT_PER_PAGE;

		const prefetched = consumeFeedPrefetch(
			feedPrefetchKey(filters, page, effectivePerPage)
		);
		if (prefetched) return prefetched;

		const primaryResult = await requestVideos(filters, page, effectivePerPage);

		if (
			!primaryResult.hasRequestError &&
			!primaryResult.items.length &&
			options.enableRecommendationFallback &&
			filters.sortBy !== SHORTS_SORT_BY.PRIORITY &&
			hasRecommendationReference(filters)
		) {
			return requestVideos(
				withDisplayInHomeShortsFallback(filters),
				page,
				effectivePerPage
			);
		}

		return primaryResult;
	}

	/**
	 * Starts the featured-videos request ahead of a likely navigation (e.g.
	 * hover on the header shorts button). Fire-and-forget: the result is only
	 * held for a fetchVideos call with identical filters/page/perPage within
	 * the TTL; on failure the entry clears itself so the real navigation
	 * always falls back to a normal fetch.
	 */
	function prefetchVideos(
		filters: ShortsFilters = {},
		page: number = 1,
		perPage?: number,
		options: FetchShortsOptions = {}
	): void {
		if (!import.meta.client) return;

		const effectivePerPage = perPage ?? SHORTS_DEFAULT_PER_PAGE;
		const key = feedPrefetchKey(filters, page, effectivePerPage);
		if (
			feedPrefetch?.key === key &&
			Date.now() - feedPrefetch.ts <= PREFETCH_TTL_MS
		) {
			return;
		}

		const promise = fetchVideos(filters, page, effectivePerPage, options);
		feedPrefetch = { key, promise, ts: Date.now() };
		// requestVideos reports failures via hasRequestError instead of
		// throwing — drop those too, so an error result is never replayed to
		// the real navigation.
		promise
			.then(result => {
				if (
					(result as InternalFeedPage).hasRequestError &&
					feedPrefetch?.key === key
				) {
					feedPrefetch = null;
				}
			})
			.catch(() => {
				if (feedPrefetch?.key === key) feedPrefetch = null;
			});
	}

	return { fetchVideos, prefetchVideos };
}
